import { Button, Card, CardTitle, cn, VirtualizedDataTable } from "@tellescope/ui";
import { useCallQueuesPanel } from "./useCallQueuesPanel";
import { useTranslation } from "react-i18next";
import { CallHoldQueueSheet } from "./CreateCallHoldQueueSheet";
import { useCallback, useMemo, useRef } from "react";
import { CALL_QUEUE_NOTIFICATION_SRC } from "./constants";
import { PermissionWrapper } from "../../../permissions";
import { useAudioWithAutoplayHandling } from "../../../hooks/useAudioWithAutoplayHandling";

export interface CallQueuesPanelProps {
  height?: string;
}

export const CallQueuesPanel = ({ height = "min-content" }: CallQueuesPanelProps) => {
  const { t } = useTranslation("call-queues");
  const audioRef = useRef<HTMLAudioElement>(null);
  const { playAudio } = useAudioWithAutoplayHandling();

  const playNewCallSound = useCallback(() => {
    playAudio(audioRef.current);
  }, [playAudio]);

  const {
    containerRef,
    virtualizedTableProps,
    isCreateEditSheetOpen,
    setIsCreateEditSheetOpen,
    editingQueueId,
    showFullList,
    setShowFullList,
    hiddenRowCount,
    hiddenRowsWithActiveCallsCount,
  } = useCallQueuesPanel({ playNewCallSound });

  const shouldShowTruncationButton = useMemo(() => {
    if (virtualizedTableProps.data.length > 3) return true;
    return false;
  }, [virtualizedTableProps.data.length]);

  return (
    <PermissionWrapper model="call_hold_queues" action="read">
      <div ref={containerRef}>
        <Card className="p-6 h-min">
          <div className="grid gap-1">
            <CardTitle>{t("Call Hold Queues")}</CardTitle>
          </div>
          <VirtualizedDataTable
            {...virtualizedTableProps}
            height={height}
            enableSorting
            tableClassName="border-0 h-min"
          />
          {shouldShowTruncationButton && (
            <Button
              variant="secondary"
              onClick={() => setShowFullList((prev) => !prev)}
              className={cn(
                "py-6",
                !!hiddenRowsWithActiveCallsCount && "bg-red-100 hover:bg-red-200 text-red-800 animate-red-flash"
              )}
            >
              {showFullList
                ? t("Hide")
                : `${t("+{{count}} queues", { count: hiddenRowCount })} ${
                    hiddenRowsWithActiveCallsCount
                      ? t("({{amount}} with calls)", { amount: hiddenRowsWithActiveCallsCount })
                      : ""
                  }`.trim()}
            </Button>
          )}
          <CallHoldQueueSheet
            open={isCreateEditSheetOpen}
            onOpenChange={setIsCreateEditSheetOpen}
            queueId={editingQueueId}
          />
          <audio ref={audioRef} src={CALL_QUEUE_NOTIFICATION_SRC} preload="auto" hidden />
        </Card>
      </div>
    </PermissionWrapper>
  );
};
